<template>
    <li class="cz2__add-multi">
        <div
            :aria-label="itemDescription"
            :aria-current="item.quantity > 0"
            :id="item.code"
            :class="[
                {
                    'cz2__add--selected': item.quantity > 0,
                    'selection-card--selected': item.quantity > 0,
                    'selection-cart--large': large,
                },
            ]"
            class="selection-card"
        >
            <div
                v-if="useBrandedBackground"
                class="selection-card__background"
                :style="{ backgroundImage: `url(${useBrandedBackground})` }"
            />

            <div
                :style="{ backgroundImage: `url(${item.image})` }"
                :class="{ 'cz2__muted-more': isOutOfStock(item.component) }"
                class="selection-card__img"
            />

            <div class="selection-card__img--overlay">
                <img
                    v-if="badgeImage"
                    :src="badgeImage"
                    :alt="badgeAltText"
                    class="selection-card__badge"
                >

                <selection-card-oos-overlay v-if="isOutOfStock(item.component)" />

                <inspect-modal-trigger
                    v-else
                    :aria-label-text="`${item.component?.description?.[0]?.name}`"
                    :component="isAttachPack ? item : item.component"
                    :inspectOverlay="inspectOverlay"
                    :isAttachPack="isAttachPack"
                />
            </div>
        </div>

        <button
            v-if="item.quantity === 0"
            @click="add(item)"
            :disabled="!item.canAdd || isOutOfStock(item.component)"
            :aria-label="`${$t('ADD_CTA')} ${itemDescription}`"
            type="button"
            :class="[
                { 'cz2__muted-more': isOutOfStock(item.component) },
            ]"
            class="cz2__add cz2__font-inter-bold"
        >
            {{ $t('ADD_CTA') }}
        </button>

        <div
            v-if="item.quantity > 0"
            class="cz2__quantity cz2__font-inter-black"
        >
            <button
                class="cz2__quantity__sub cz2__font-inter-black"
                @click="quantitySubtract(item)"
                :aria-label="item.quantity > 1 ? $t('QUANTITY_MINUS') : $t('QUANTITY_REMOVE')"
                type="button"
                v-html="$t('QUANTITY_MINUS_SYMBOL')"
            />

            <span class="cz2__quantity__count">
                {{ item.quantity }}
            </span>
            <span class="cz2__sr-only">
                {{ itemDescription }}
            </span>

            <button
                class="cz2__quantity__add cz2__font-inter-black"
                :disabled="!item.canAdd || item.quantity >= inventory"
                @click="quantityAdd(item)"
                :aria-label="$t('QUANTITY_PLUS')"
                type="button"
                v-html="$t('QUANTITY_PLUS_SYMBOL')"
            />
        </div>
    </li>
</template>

<style lang="scss">
    @import "../../style/variables.scss";

    #cz2.cz2 {
        .cz2__add-multi {
            button {
                transition: all 0.3s ease-in-out;
            }

            .selection-card__img,
            .selection-card__img--overlay {
                top: 0;
                left: 0;
            }
        }

        .selection-card__img--overlay {
            position: absolute;
            width: 100%;
            height: 100%;
        }

        .cz2__add {
            position: relative;

            color: color(primaryWhite);

            &:focus {
                border-color: color(secondaryBlack);
            }

            &:disabled {
                opacity: 0.5;

                cursor: not-allowed;
            }
        }

        .cz2__add,
        .cz2__quantity {
            margin-top: 16px;
            border: 3px solid color(primaryWhite);
            border-radius: 33px;
            padding: 0 16px;
            width: 100%;
            font-style: italic;
        }

        .cz2__add,
        .cz2__quantity,
        .cz2__quantity__sub,
        .cz2__quantity__count,
        .cz2__quantity__add {
            text-align: center;
            font-size: 24px;
            letter-spacing: -0.89px;
        }

        .cz2__quantity__sub,
        .cz2__quantity__count,
        .cz2__quantity__add {
            color: color(v2BlueDark);
        }

        .cz2__quantity {
            border: 3px solid color(v2YellowLight);

            background: white;

            display: flex;
            justify-content: space-between;
        }

        .cz2__quantity__sub:disabled,
        .cz2__quantity__add:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__add {
            margin-top: 32px;

            font-size: 48px;

            border-width: 5px;
        }

        .cz2__quantity {
            margin-top: 32px;

            border-width: 5px;
        }

        .cz2__quantity__count {
            font-size: 48px;
        }

        .cz2__quantity__add {
            font-size: 48px;
        }

        .cz2__quantity__sub {
            font-size: 48px;
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //   ▗▄▄▖▗▄▄▖ ▗▄▄▄▖▗▖   ▗▖   ▗▄▄▄▖▗▖  ▗▖ ▗▄▄▖    ▗▄▄▖ ▗▄▄▄▖▗▄▄▄▖
    //  ▐▌   ▐▌ ▐▌▐▌   ▐▌   ▐▌     █  ▐▛▚▖▐▌▐▌       ▐▌ ▐▌▐▌   ▐▌
    //   ▝▀▚▖▐▛▀▘ ▐▛▀▀▘▐▌   ▐▌     █  ▐▌ ▝▜▌▐▌▝▜▌    ▐▛▀▚▖▐▛▀▀▘▐▛▀▀▘
    //  ▗▄▄▞▘▐▌   ▐▙▄▄▖▐▙▄▄▖▐▙▄▄▖▗▄█▄▖▐▌  ▐▌▝▚▄▞▘    ▐▙▄▞▘▐▙▄▄▖▐▙▄▄
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    #cz2.cz2.cz2__is-sb {
        .cz2__add-multi {
            .cz2__add, .cz2__quantity {
                background-color: #fff;
                color: color(sbDarkBlue);
                border-color: color(sbDarkRustRed);
                border-radius: 3px;
                font-style: normal;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                font-size: 24px;

                border-radius: 4px;
                border-color: #9A7D4B;

                justify-content: center;
                gap: 24px;
            }

            .cz2__quantity {
                border-color: color(sbGold);
            }

            .cz2__quantity__sub,
            .cz2__quantity__add {
                font-family: 'Proxima Nova', sans-serif;
                font-weight: 900;
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk.cz2__is-sb {
        .cz2__add-multi {
            .cz2__quantity {
                margin-top: 15px;
            }

            .cz2__quantity__count {
                font-size: 40px;
            }

            .cz2__quantity__add {
                font-size: 40px;
            }

            .cz2__quantity__sub {
                font-size: 40px;
            }

            .cz2__add {
                margin-top: 15px;

                font-size: 40px;
            }
        }
    }
</style>

<script>
    import { getAts } from '../../helpers/productData';

    import InspectModalTrigger from '../InspectModalTrigger.vue';
    import SelectionCardOosOverlay from './SelectionCardOosOverlay.vue';

    export default {
        components: {
            InspectModalTrigger,
            SelectionCardOosOverlay,
        },

        props: {
            item: Object,
            itemDescription: String,
            inspectOverlay: {
                type: Boolean,
                default: false,
            },
            isAttachPack: {
                type: Boolean,
                default: false,
            },
            large: Boolean,
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            inventory() {
                return getAts(this.$store.state, this.item.component, this.viewController);
            },

            headerFontClass() {
                return this.$store.getters.headerFontClass;
            },

            component() {
                return this.item.component;
            },

            useBrandedBackground() {
                if (this.component && this.component.custom?.['fun-brand-background']) {
                    return this.component && this.component.custom?.['fun-brand-background'];
                }

                return null;
            },

            badgeImage() {
                return this.item.component?.custom?.['fun-badge-image'];
            },

            badgeAltText() {
                const key = this.item.component?.custom?.['fun-badge-alt-text'];

                if (key?.length > 0) {
                    if (this.$te(key) || this.$te(key, this.$i18n.fallbackLocale)) {
                        return this.$t(key);
                    }

                    return key;
                }

                return this.item.component?.custom?.['fun-badge-alt-text'] || '';
            },
        },

        methods: {
            add(item) {
                this.$emit('add', item);
            },

            quantityAdd(item) {
                this.$emit('quantityAdd', item);
            },

            quantitySubtract(item) {
                this.$emit('quantitySubtract', item);
            },

            isOutOfStock(item) {
                return this.viewController.isAllowed(item) && !this.viewController.isAvailable(item);
            },
        },
    };
</script>
